import React, { useState, useEffect, useRef } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { center } from "@cloudinary/url-gen/qualifiers/textAlignment";

interface NumberInputProps {
    id: string;
    label: string;
    value: number | null | undefined;
    onChange: (value: number | null) => void;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    error?: string | null;
    onInputChange?: (value: string) => void;
}

const NumberInput: React.FC<NumberInputProps> = ({
    id,
    label,
    value,
    onChange,
    min = -Infinity,
    max = Infinity,
    step = 1,
    disabled = false,
    error = null,
    onInputChange,
}) => {
    const [internalValue, setInternalValue] = useState<string>(String(value ?? ""));
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (String(value ?? "") !== internalValue) {
            setInternalValue(String(value ?? ""));
        }
    }, [value]);

    const clampValue = (val: number) => Math.max(min, Math.min(max, val));

    const changeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const triggerChangeWithDelay = (newValue: number | null) => {
        if (changeTimeout.current) {
            clearTimeout(changeTimeout.current);
        }
        changeTimeout.current = setTimeout(() => {
            onChange(newValue);
        }, 500);
    };

    const handleIncrease = () => {
        setInternalValue(prev => {
            const numValue = Number(prev || 0);
            const newValue = clampValue(numValue + step);
            triggerChangeWithDelay(newValue);
            return String(newValue);
        });
    };

    const handleDecrease = () => {
        setInternalValue(prev => {
            const numValue = Number(prev || 0);
            const newValue = clampValue(numValue - step);
            triggerChangeWithDelay(newValue);
            return String(newValue);
        });
    };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (/^-?\d*\.?\d*$/.test(val)) {
            setInternalValue(val);
            onInputChange?.(val);
        }
    };

    const handleBlur = () => {
        // Cancel delay and flush immediately on blur
        if (changeTimeout.current) {
            clearTimeout(changeTimeout.current);
        }
        if (internalValue.trim() === "") {
            onChange(null);
            return;
        }
        const parsed = parseFloat(internalValue);
        if (!isNaN(parsed)) {
            onChange(clampValue(parsed));
        } else {
            onChange(null);
        }
    };

    return (
        <TextField
            id={id}
            label={label}
            value={internalValue}
            onChange={handleChange}
            onBlur={handleBlur}
            type="text"
            disabled={disabled}
            fullWidth
            error={!!error}
            helperText={error}
            inputRef={inputRef}
            inputProps={{
                pattern: "[0-9]*\\.?[0-9]*",
                inputMode: "decimal",
            }}
            sx={{
                "& input": {
                    textAlign: "center"
                }
            }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <IconButton
                            onPointerDown={handleDecrease}
                            disabled={disabled}>
                            <RemoveIcon />
                        </IconButton>
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            onPointerDown={handleIncrease}
                            disabled={disabled}>
                            <AddIcon />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );
};

export default NumberInput;
